import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { formatRupiah, groupSelected } from "@/lib/dirga";
import { cn } from "@/lib/utils";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="font-sans text-[11px] uppercase tracking-[0.25em] text-[#8A8A8A]">
      {label}
      {required && <span className="ml-1 text-[#1C1C1C]">*</span>}
    </span>
    {children}
  </label>
);

const inputCls =
  "mt-2 w-full border-0 border-b border-[#D8D2C6] bg-transparent py-3 font-sans text-base text-[#1C1C1C] outline-none transition-colors placeholder:text-[#C4BEB2] focus:border-[#1C1C1C]";

export const ClientForm = ({ selectedIds, total, defaultEventType, onSubmit, submitting }) => {
  const [form, setForm] = useState({
    fullName: "",
    whatsapp: "",
    instagram: "",
    eventDate: "",
    eventLocation: "",
    eventType: defaultEventType || "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [eventTypeTouched, setEventTypeTouched] = useState(false);

  // Auto-populate Event Type from selected moments, unless the user edited it.
  useEffect(() => {
    if (!eventTypeTouched) {
      setForm((f) => ({ ...f, eventType: defaultEventType || "" }));
    }
  }, [defaultEventType, eventTypeTouched]);

  const update = (key) => (e) => {
    if (key === "eventType") setEventTypeTouched(true);
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.fullName.trim()) next.fullName = "Mohon isi nama kamu.";
    const digits = form.whatsapp.replace(/\D/g, "");
    if (!form.whatsapp.trim()) next.whatsapp = "Mohon isi nomor WhatsApp.";
    else if (digits.length < 8) next.whatsapp = "Nomor sepertinya terlalu pendek.";
    if (!form.eventDate) next.eventDate = "Mohon pilih tanggal acara.";
    if (!form.eventLocation.trim()) next.eventLocation = "Mohon isi lokasi acara.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    onSubmit(form);
  };

  const groups = groupSelected(selectedIds);

  return (
    <form onSubmit={handleSubmit} className="mx-auto w-full max-w-2xl" data-testid="client-form">
      <p className="font-sans text-xs uppercase tracking-[0.35em] text-[#B9B2A4]">
        Suka dengan paket yang kamu buat?
      </p>
      <h2 className="mt-4 font-serif text-4xl font-light tracking-tight text-[#1C1C1C] sm:text-5xl">
        Ceritakan Tentang Hari Spesialmu
      </h2>
      <p className="mt-4 font-sans text-sm text-[#8A8A8A]">
        Ceritakan sedikit tentang rencanamu, dan biar kami yang urus sisanya.
      </p>

      {/* Recap */}
      <div className="mt-10 border border-[#E8E3D9] bg-white p-6" data-testid="form-recap">
        <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-[#1C1C1C]">
          Pilihan Kamu
        </p>
        <div className="mt-4 space-y-4">
          {Object.keys(groups).length === 0 ? (
            <p className="font-sans text-sm text-[#8A8A8A]">Belum ada layanan yang dipilih.</p>
          ) : (
            Object.entries(groups).map(([cat, items]) => (
              <div key={cat}>
                <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#B9B2A4]">
                  {cat}
                </p>
                {items.map((it) => (
                  <div key={it.id} className="flex justify-between font-sans text-sm text-[#1C1C1C]">
                    <span>{it.name}</span>
                    <span>{formatRupiah(it.price)}</span>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
        <div className="mt-5 flex items-baseline justify-between border-t border-[#E8E3D9] pt-4">
          <span className="font-sans text-[11px] uppercase tracking-[0.25em] text-[#8A8A8A]">
            Estimasi Biaya
          </span>
          <span className="font-serif text-2xl text-[#1C1C1C] whitespace-nowrap" data-testid="form-total">
            {formatRupiah(total)}
          </span>
        </div>
      </div>

      {/* Fields */}
      <div className="mt-10 space-y-8">
        <div>
          <Field label="Nama Lengkap" required>
            <input
              data-testid="input-fullname"
              value={form.fullName}
              onChange={update("fullName")}
              placeholder="Nama lengkap kamu"
              className={inputCls}
            />
          </Field>
          {errors.fullName && <p className="mt-1.5 font-sans text-xs text-red-600" data-testid="error-fullname">{errors.fullName}</p>}
        </div>

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
          <div>
            <Field label="Nomor WhatsApp" required>
              <input
                data-testid="input-whatsapp"
                value={form.whatsapp}
                onChange={update("whatsapp")}
                inputMode="tel"
                placeholder="08xxxxxxxxxx"
                className={inputCls}
              />
            </Field>
            {errors.whatsapp && <p className="mt-1.5 font-sans text-xs text-red-600" data-testid="error-whatsapp">{errors.whatsapp}</p>}
          </div>
          <div>
            <Field label="Instagram">
              <input
                data-testid="input-instagram"
                value={form.instagram}
                onChange={update("instagram")}
                placeholder="@akunkamu"
                className={inputCls}
              />
            </Field>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
          <div>
            <Field label="Tanggal Acara" required>
              <input
                type="date"
                data-testid="input-eventdate"
                value={form.eventDate}
                onChange={update("eventDate")}
                className={cn(inputCls, "appearance-none")}
              />
            </Field>
            {errors.eventDate && <p className="mt-1.5 font-sans text-xs text-red-600" data-testid="error-eventdate">{errors.eventDate}</p>}
          </div>
          <div>
            <Field label="Lokasi Acara" required>
              <input
                data-testid="input-location"
                value={form.eventLocation}
                onChange={update("eventLocation")}
                placeholder="Kota / venue"
                className={inputCls}
              />
            </Field>
            {errors.eventLocation && <p className="mt-1.5 font-sans text-xs text-red-600" data-testid="error-location">{errors.eventLocation}</p>}
          </div>
        </div>

        <div>
          <Field label="Jenis Acara">
            <input
              data-testid="input-eventtype"
              value={form.eventType}
              onChange={update("eventType")}
              placeholder="mis. Akad & Resepsi"
              className={inputCls}
            />
          </Field>
        </div>

        <div>
          <Field label="Catatan Tambahan">
            <textarea
              data-testid="input-notes"
              value={form.notes}
              onChange={update("notes")}
              rows={4}
              placeholder="Ceritakan apa pun yang ingin kamu sampaikan..."
              className={cn(inputCls, "resize-none")}
            />
          </Field>
        </div>
      </div>

      <button
        type="submit"
        data-testid="send-request-btn"
        disabled={submitting}
        className="group mt-12 flex w-full items-center justify-center gap-3 bg-[#1C1C1C] px-8 py-5 font-sans text-sm uppercase tracking-widest text-white transition-colors duration-300 hover:bg-[#333333] disabled:opacity-60"
      >
        {submitting ? "Mengirim..." : "Kirim Permintaan Saya"}
        {!submitting && (
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        )}
      </button>

      <p className="mt-6 text-center font-sans text-xs leading-relaxed text-[#B9B2A4]">
        Ini hanya estimasi biaya. Penawaran final akan dikonfirmasi berdasarkan
        detail acara, tanggal, lokasi, dan ketersediaan.
      </p>
    </form>
  );
};
